import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { FaFile, FaPaperPlane } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { ChatContext } from "../../../contexts/user/ChatContext";
import { StyledFileSendForm } from "./style";
import { CustomState } from "../../../Hooks/State.hook";
import { SpecificAttendanceDetails } from "../../../interfaces/Attendances.interfaces";
import Input from "../../Input";
import { defaultInput100 } from "../../../components-variants/defaultInputs";
import { FileSizeModal } from "./FileSizeModal";

interface Props {
    attendance: SpecificAttendanceDetails;
}

export const FileSendForm = ({ attendance }: Props) => {
    const {
        closeFile,
        sendMessage,
        formData
    } = useContext(ChatContext);

    const filePreview = CustomState<ReactNode>("");
    const textInputRef = useRef<HTMLInputElement>(null);
    const filePreviewRef = useRef<HTMLDivElement>(null);

    const [showSizeModal, setShowSizeModal] = useState(false);
    const [awaitingSend, setAwaitingSend] = useState(false);

    useEffect(() => {
        if (!formData.value.isAudio) {
            const file = formData.value.file;
            if (!file) {
                filePreview.set(<></>);
                return;
            }

            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                setShowSizeModal(true);
                return;
            }

            renderPreview(file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.value.file]);

    const renderPreview = (file: File) => {
        if (filePreviewRef.current) {
            filePreviewRef.current.innerHTML = "";
        }

        const isImage = file.type.includes("image");
        const isVideo = file.type.includes("video");
        const isAudio = file.type.includes("audio");

        if (isImage) {
            const img = document.createElement("img");
            img.src = URL.createObjectURL(file);
            img.alt = file.name;
            filePreviewRef.current?.appendChild(img);
        } else if (isVideo) {
            const video = document.createElement("video");
            const source = document.createElement("source");
            source.src = URL.createObjectURL(file);
            source.type = file.type;
            video.appendChild(source);
            video.controls = true;
            filePreviewRef.current?.appendChild(video);
        } else if (isAudio) {
            const audio = document.createElement("audio");
            audio.controls = true;
            audio.src = URL.createObjectURL(file);
            filePreviewRef.current?.appendChild(audio);
        } else {
            filePreview.set(
                <div className="no_preview">
                    <FaFile />
                    <span> Sem prévia disponível...</span>
                </div>
            );
        }
    };

    function onClickSend() {
        sendMessage(attendance);
        closeFile();
    }

    function onConfirmLargeFile() {
        setShowSizeModal(false);
        setAwaitingSend(true);
        // Você pode setar aqui uma flag para "enviar como link"
        // formData.set(prev => ({ ...prev, convertToLink: true }));
        const file = formData.value.file;
        if (file) {
            renderPreview(file);
        }
    }

    function onCancelLargeFile() {
        setShowSizeModal(false);
        closeFile();
    }

    return (
        <>
            {showSizeModal && (
            <FileSizeModal
                onConfirm={onConfirmLargeFile}
                onCancel={onCancelLargeFile}
            />
            )}

            <StyledFileSendForm onSubmit={(e) => {
                e.preventDefault();
                onClickSend();
            }}>
                <header>
                    <button type="button" onClick={closeFile}>
                        <IoMdClose />
                    </button>
                    <h2>{formData.value.file?.name}</h2>
                </header>

                <main>
                    <div className="file_preview" ref={filePreviewRef}>
                        {filePreview.value}
                    </div>
                    <div className="message_camp">
                        <Input
                            placeholder="Digite sua mensagem..."
                            value={formData.value.text}
                            onChange={(e) => formData.set((prev) => ({
                                ...prev,
                                text: e.target.value
                            }))}
                            onSubmit={(e) => e.preventDefault()}
                            ref={textInputRef}
                            {...defaultInput100}
                            $padding={[1, 1]}
                        />
                        <button
                            className="sendFile"
                            onClick={onClickSend}
                            type="button"
                        >
                            <FaPaperPlane />
                        </button>
                    </div>
                </main>
            </StyledFileSendForm>
        </>
    );
};
